import React, { FC, memo, useCallback, useEffect, useState } from 'react'

import { useSetFocus } from '@please/lrud'
import { useLocation } from 'react-router-dom'

import { updateCreditWallets, updateCurrentEventById } from '@/actions'
import { ChannelImage } from '@/components/ChannelImage/ChannelImage'
import { EventHeading } from '@/components/EventHeading'
import { Header } from '@/components/Header'
import { LoadableComponent } from '@/components/LoadableComponent'
import { PageBackground } from '@/components/PageContent/PageBackground'
import { focusIds, orientation } from '@/constants/focus'
import { LoginSessionType } from '@/constants/login'
import { paths } from '@/constants/paths'
import { useEventChannel, useRedeemablePPVEvent } from '@/hooks'
import { useFindNearestByImgWidth } from '@/hooks/useFindNearestByImgWidth'
import { usePageRefresher } from '@/hooks/usePageRefresher'
import { useCustomPalette } from '@/hooks/useSetPalette'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { EventDetailPageProps } from '@/types/componentsPropsTypes'
import { eventTypes } from '@/types/servicesTypes/configTypes'

import { EventActionDisplay } from './EventActionDisplay'
import { EventDetailsSlider } from './EventDetailsSlider'
import { EventImage } from './EventImage'
import { useEventDetailsGA, useEventStatus } from './hooks'
import {
  BGContainer,
  BodyText,
  BodyTextWrapper,
  ChannelLogo,
  ContentContainer,
  ImageBoxWrapper,
  InfoBox,
  EventUnavailableHeader,
} from './styles'

const focusId = `${focusIds.EVENT_PAGE}${focusIds.CONTAINER_MOD}`

const EventDetailPageRaw: FC<EventDetailPageProps> = ({ isPopup }) => {
  const { t } = useCommonTranslation()
  const setFocus = useSetFocus()
  const location = useLocation()

  const [isEventActionMounted, setIsEventActionMounted] = useState(false)
  const listPopup = useStore((state) => state.common.listPopup)
  const currentEvent = useStore((state) => state.common.currentEvent)
  const allChannels = useStore((state) => state.common.allChannels)
  const setAuthPopup = useStore((state) => state.common.setAuthPopup)
  const { pageRefresh } = usePageRefresher()
  const eventChannel = useEventChannel(currentEvent)

  const currentEventStatus = useEventStatus(currentEvent)
  let isRedeemablePPVEvent = useRedeemablePPVEvent(currentEvent)

  isRedeemablePPVEvent = true // Show Unlock Now button for everyone

  const eventIdFromUrl = location.pathname.split('/').pop()

  const isYouTube = currentEvent?.type === eventTypes.YOUTUBE
  const eventImg = useFindNearestByImgWidth(currentEvent?.image?.sources, 786, {
    targetAspectRatio: isYouTube ? '' : '16/9',
  })

  const eventDescription =
    currentEvent?.description?.long || currentEvent?.description?.short

  useEventDetailsGA(currentEvent, allChannels)

  useEffect(() => {
    const controller = new AbortController()
    if (currentEvent?.ppvInfo) {
      void updateCreditWallets(controller.signal)
    }

    return () => {
      controller?.abort()
    }
  }, [currentEvent])

  useEffect(() => {
    if (currentEvent || !eventIdFromUrl) {
      return
    }
    const controller = new AbortController()
    void updateCurrentEventById(eventIdFromUrl, controller.signal)

    return () => {
      controller.abort()
    }
  }, [currentEvent, eventIdFromUrl])

  useEffect(() => {
    !listPopup && isEventActionMounted && setTimeout(setFocus, 0, focusId)
  }, [listPopup, isEventActionMounted])

  const { pageImgSrc } = useCustomPalette()

  const channelLogoSources =
    currentEvent?.channelLogo?.sources || eventChannel?.image?.sources
  const canShowEventAction =
    currentEvent &&
    currentEventStatus &&
    isRedeemablePPVEvent !== undefined &&
    eventChannel !== undefined

  const goToLogin = useCallback(() => {
    if (!currentEvent) {
      return
    }
    setAuthPopup({
      sessionType: LoginSessionType.LOGIN,
      prevFocusId: focusId,
      redirectPath: isPopup ? `${paths.EVENT_PAGE}/${currentEvent.id}` : '',
      onSuccessHandler: isPopup ? undefined : pageRefresh,
    })
  }, [currentEvent?.id])

  return (
    <BGContainer
      focusId={focusId}
      elementType="div"
      orientation={orientation.VERTICAL}
      defaultFocusChild={0}
    >
      {pageImgSrc && <PageBackground src={pageImgSrc} />}
      <LoadableComponent isLoading={!allChannels}>
        <Header className="content-wrapper" />
        <ContentContainer>
          <InfoBox>
            {currentEvent ? (
              <EventHeading currentEventStatus={currentEventStatus} />
            ) : (
              <EventUnavailableHeader>
                {t('detailPage.eventNotFound')}
              </EventUnavailableHeader>
            )}
            <BodyTextWrapper>
              <BodyText>{eventDescription}</BodyText>
            </BodyTextWrapper>
            {canShowEventAction && (
              <EventActionDisplay
                event={currentEvent}
                eventChannel={eventChannel}
                setIsEventActionMounted={setIsEventActionMounted}
                channelLogoSources={channelLogoSources}
                currentEventStatus={currentEventStatus}
                isRedeemablePPVEvent={isRedeemablePPVEvent}
                parentFocusId={focusId}
                goToLogin={goToLogin}
              />
            )}
          </InfoBox>
          <ImageBoxWrapper>
            {!!(eventImg?.url && currentEvent?.name) && (
              <EventImage src={eventImg?.url} withCover={isYouTube} />
            )}
            <ChannelLogo>
              <ChannelImage sources={channelLogoSources} />
            </ChannelLogo>
          </ImageBoxWrapper>
        </ContentContainer>
        {
          // conditional render needed in order to preserve correct focus tree positions
          isEventActionMounted && <EventDetailsSlider isPopup={!!isPopup} />
        }
      </LoadableComponent>
    </BGContainer>
  )
}

export const EventDetailPage = memo(EventDetailPageRaw)
